import React, { useState, useMemo } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Search, TestTube2, Package, ChevronDown } from "lucide-react";

// A grouped, searchable picker for individual lab tests. `catalog` is the same
// LAB_CATALOG shape used in PatientDetail.
export function LabCatalogPicker({ catalog, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    if (!q.trim()) return catalog;
    const needle = q.trim().toLowerCase();
    return catalog
      .map((g) => ({ ...g, tests: g.tests.filter((t) => t.name.toLowerCase().includes(needle)) }))
      .filter((g) => g.tests.length > 0);
  }, [catalog, q]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-8" data-testid="lab-catalog-open">
          <TestTube2 size={12} className="text-pink-600" />
          Add test from catalog
          <ChevronDown size={11} className="opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 max-h-[520px] overflow-hidden flex flex-col" align="start">
        <div className="p-2 border-b border-slate-200 shrink-0">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 90+ tests…"
              autoFocus
              className="h-8 pl-7 text-sm"
              data-testid="lab-catalog-search"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {groups.length === 0 && (
            <div className="p-6 text-center text-xs text-slate-500">No tests match "{q}"</div>
          )}
          {groups.map((g) => (
            <div key={g.group} className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
                {g.group}
              </div>
              {g.tests.map((t) => (
                <button
                  key={t.name}
                  onClick={() => { onAdd(t.name); setOpen(false); setQ(""); }}
                  className="w-full px-3 py-1.5 text-left hover:bg-pink-50 text-xs flex items-center justify-between gap-2"
                  data-testid={`lab-catalog-item-${t.name.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  <span className="text-slate-800 font-medium">{t.name}</span>
                  <span className="text-[10px] text-slate-500 tabular shrink-0">
                    {t.unit && <span>{t.unit} · </span>}{t.ref}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// One-click package picker — adds an entire profile of tests at once.
export function LabPackagePicker({ packages, onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-8" data-testid="lab-package-open">
          <Package size={12} className="text-emerald-600" />
          Lab package
          <ChevronDown size={11} className="opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-1" align="start">
        <div className="max-h-96 overflow-y-auto">
          {packages.map((p) => (
            <button
              key={p.key}
              onClick={() => { onAdd(p); setOpen(false); }}
              className="w-full px-2.5 py-2 text-left hover:bg-emerald-50 rounded text-xs"
              data-testid={`lab-package-${p.key}`}
            >
              <div className="font-medium text-slate-800">{p.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{p.tests.length} tests</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
